import _ from 'lodash'
import { Maybe, Dictionary } from '@/types/general'
import Entry from '@/data/entry-model'
import PStorage, { SMART_SEARCH } from './PStorage'

interface CharIndex {
    char: string
    index: number
    case: boolean
}

interface CharIndexScore {
    indices: CharIndex[]
    score: number
}

interface IndexScore {
    indices: number[]
    score: number
}

export const SMART_SEARCH_MAX = 8

class Search {
    /**
     * Get the search score for a Searchable entry
     *
     * @param {string} term search term
     * @param {Entry} entry entry to search
     * @returns {number} score
     * @memberof Search
     */
    public score(term: string, entry: Entry, tags?: string[], fields?: Dictionary<string>): number {
        let score = 0

        if (entry.titlePath) {
            const fuzz = this.fuzz(term, `${entry.titlePath.join(' / ')}`)
            if (fuzz) {
                if (fuzz.indices.includes(-1)) {
                    score -= 5
                }
                score += fuzz.score
            }
        }
        if (tags && entry.tags) {
            tags.forEach((searchTag) => {
                entry.tags.forEach((tag) => {
                    if (tag.includes(searchTag)) {
                        score += 2
                    }
                })
            })
        }
        if (fields) {
            Object.keys(fields).forEach((key) => {
                if (entry.fields[key]
                    && entry.fields[key].toLowerCase().includes(fields[key])) {
                    score += 10
                }
            })
        }
        return score
    }

    /**
     * Get the indices of matches in a 'fuzzy' search
     *
     * @param {string} term search term
     * @param {string} text text to search
     * @returns {number[]} indices
     */
    public fuzz(term: string, text: string): Maybe<IndexScore> {
        if (PStorage.get(SMART_SEARCH) && term.length <= SMART_SEARCH_MAX) {
            const results = this.smartFuzz(term, text)
            if (results.length === 0) {
                return undefined
            }
            const best = _.maxBy(results, 'score') as CharIndexScore
            return { score: best.score, indices: best.indices.map(i => i.index) }
        }
        text = text.toLowerCase()
        term = term.toLowerCase()
        let termIndex = 0
        const indices: number[] = []
        _.each(text, (char: string, index: number): boolean => {
            if (term[termIndex] === char) {
                termIndex += 1
                indices.push(index)
                return termIndex !== term.length
            }
            return true
        })
        return { indices, score: termIndex === term.length ? indices.length : 0 }
    }

    /**
     * Get all permutations of the term characters in the string
     */
    public perms(term: string, text: string): CharIndex[][] {
        const locations: Dictionary<CharIndex[]> = {}
        _.each(term, (char: string, termIndex: number) => {
            locations[termIndex] = []
            _.each(text, (textChar: string, index: number) => {
                if (char === textChar) {
                    locations[termIndex].push({
                        char,
                        index,
                        case: true
                    })
                } else if (char.toLowerCase() === textChar.toLowerCase()) {
                    locations[termIndex].push({
                        char,
                        index,
                        case: false
                    })
                }
            })
            if (locations[termIndex].length === 0) {
                locations[termIndex].push({
                    char,
                    index: -1,
                    case: false
                })
            }
        })
        return [...this.cartesian(...Object.values(locations))]
    }

    /**
     * Cartesian product to get all combinations of the given arrays.
     * Source: le_m
     * https://stackoverflow.com/questions/4331092/finding-all-combinations-of-javascript-array-values
     */
    public* cartesian(head: any[] = [], ...tail: any[]): IterableIterator<any> {
        const remainder = tail.length ? this.cartesian(...tail) : [[]]
        // eslint-disable-next-line no-restricted-syntax
        for (const r of remainder) for (const h of head) yield [h, ...r]
    }

    /**
     * Smart fuzzy search algorithm.
     *
     * Returns a list of objects with matching indices and the score for
     * the indices dependent on their position in the text
     */
    public smartFuzz(term: string, text: string): CharIndexScore[] {
        return _.map(this.perms(term, text), (charIndices: CharIndex[]): CharIndexScore => {
            let score = 0
            let prevIndex = -1
            _.each(charIndices, (charIndex: CharIndex) => {
                if (charIndex.index === 0) {
                    // favour start of text
                    // 1 is also added later as part of sequential check
                    score += 1
                } else if (text.charAt(charIndex.index - 1) === ' ') {
                    // favour start of words
                    score += 1
                }
                if (charIndex.case) {
                    // favour capitalization
                    score += 1
                }
                if (prevIndex < charIndex.index && prevIndex !== -1) {
                    // favour ordered characters
                    score += 1
                }
                if (prevIndex === charIndex.index - 1) {
                    // favour sequential characters
                    score += 1
                }
                prevIndex = charIndex.index
            })
            return {
                indices: charIndices,
                score
            }
        })
    }
}

export default new Search()
